import { Link } from 'react-router-dom';
import BlueDotsBackground from '../BlueDotsBackground';

export default function RebateModel() {
  return (
    <>
      <BlueDotsBackground />

      <div className="min-h-screen text-white px-6 py-12 space-y-24">
        {/* Hero */}
        <section className="text-center max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Greater Visibility Into 340B Covered Entity Claims Data
          </h1>

          <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-10">
            The 340B Rebate Model Pilot Program submission deadline is
            approaching on August 24. While many manufacturers are expected to
            submit similar rebate models, one important challenge remains:
            limited access to the underlying covered entity claims data.
          </p>

          <div className="grid md:grid-cols-3 gap-6 text-center">
            {[
              {
                title: 'Data Transparency',
                text: 'Limited visibility into the underlying covered entity claims data.',
                icon: 'fas fa-eye-slash',
              },
              {
                title: 'Financial and Compliance Impact',
                text: 'Challenges supporting accurate GTN and GP calculations.',
                icon: 'fas fa-scale-balanced',
              },
              {
                title: 'Governance and Oversight',
                text: 'Limited ability to independently audit, reconcile, and validate rebates.',
                icon: 'fas fa-clipboard-check',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-xl p-5 flex flex-col items-center"
              >
                <i className={`${item.icon} text-blue-400 mb-3`}></i>
                <p>
                  <span className="font-semibold text-blue-400">
                    {item.title}:
                  </span>{' '}
                  {item.text}
                </p>
              </div>
            ))}
          </div>

          <p className="text-lg text-gray-300 max-w-4xl mx-auto mt-10">
            We have been exploring an approach that may provide greater
            visibility into covered entity claims data and help address these
            challenges. If this is an area of interest, we welcome the
            opportunity to discuss our perspective.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
            <Link
              to="/contact"
              className="inline-block bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-xl font-semibold transition"
            >
              Discuss Our Approach
            </Link>
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById('cycle')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
              className="inline-block border border-blue-500 text-blue-400 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-semibold transition"
            >
              340B Cycle
            </button>
          </div>
        </section>

        {/* Rebate Cycle Diagram */}
        <section id="cycle" className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            The 340B Rebate Claim Cycle
          </h2>

          {(() => {
            const cycle = [
              { label: 'Covered Entity', icon: 'fas fa-hospital' },
              { label: 'Claim Aggregator', icon: 'fas fa-layer-group' },
              { label: 'Manufacturer', icon: 'fas fa-industry' },
              {
                label: 'Claim Processing',
                icon: 'fas fa-magnifying-glass-chart',
              },
              { label: 'Payment', icon: 'fas fa-money-bill-wave' },
            ];
            const nodeRadius = 42; // percent from center
            const arcRadius = 46; // larger radius for curved arrows
            const polar = (angle, r) => ({
              x: 50 + r * Math.cos(angle),
              y: 50 + r * Math.sin(angle),
            });
            return (
              <div className="relative mx-auto aspect-square max-w-[520px]">
                <svg
                  viewBox="0 0 100 100"
                  className="absolute inset-0 w-full h-full overflow-visible"
                >
                  <defs>
                    <marker
                      id="cycleArrow"
                      viewBox="0 0 10 10"
                      refX="8"
                      refY="5"
                      markerWidth="3"
                      markerHeight="3"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill="#2563eb" />
                    </marker>
                  </defs>
                  {cycle.map((node, index) => {
                    const gap = 0.33; // leave space near nodes; larger gap = shorter arcs
                    const a1 =
                      ((index + gap) / cycle.length) * 2 * Math.PI -
                      Math.PI / 2;
                    const a2 =
                      ((index + 1 - gap) / cycle.length) * 2 * Math.PI -
                      Math.PI / 2;
                    const start = polar(a1, arcRadius);
                    const end = polar(a2, arcRadius);
                    return (
                      <path
                        key={`arc-${index}`}
                        d={`M ${start.x} ${start.y} A ${arcRadius} ${arcRadius} 0 0 1 ${end.x} ${end.y}`}
                        fill="none"
                        stroke="#2563eb"
                        strokeWidth="2"
                        markerEnd="url(#cycleArrow)"
                      />
                    );
                  })}
                </svg>
                {cycle.map((node, index) => {
                  const angle =
                    (index / cycle.length) * 2 * Math.PI - Math.PI / 2;
                  const pos = polar(angle, nodeRadius);
                  return (
                    <div
                      key={node.label}
                      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-center w-32"
                      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                    >
                      <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center mb-2">
                        <i className={`${node.icon} text-white text-2xl`}></i>
                      </div>
                      <span className="text-sm font-semibold">
                        {node.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </section>
      </div>
    </>
  );
}
